import React, {useEffect, useState} from 'react';
import _ from 'lodash';
import ApartmentIcon from '@mui/icons-material/Apartment';
import clsx from 'clsx';
import {IconButton} from '@mui/material';
import {useTranslation} from 'react-i18next';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useDispatch, useSelector} from 'react-redux';
import {getConfig} from '../../../../util/configUtil';
import {hasOneOfPermission, Permissions} from '../../../../store/reducers/user';
import {getAdmissionStartDate} from '../../../../util/patientUtil';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AttendanceTimelineHistory from '../../../../components/ui/timeline/attendanceTimelineHistory';
import makeStyles from '@mui/styles/makeStyles';
import {useTheme} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import usePatientLocation from '../../../../hooks/usePatientLocation';
import {ctxSplitScreen} from '../../../../store/reducers/patientContext';

const useStyles = makeStyles((theme) => ({
    attendanceListItem: {
        width: '100%',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: theme.palette.inPatient.formBackground,
        marginBottom: 10,
    },
    attendanceHeader: {
        backgroundColor: theme.palette.inPatient.inputBackground,
        padding: '5px 10px',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        '&.right': {
            marginLeft: 'auto',
        },
    },
    label: {
        fontFamily: 'Inter',
        fontWeight: 'bold',
        marginRight: 5,
        fontSize: 14,
    },
    headerText: {
        color: theme.palette.admissionForm.labelColor,
    },
    location: {
        backgroundColor: '#03C3EC',
        borderRadius: 3,
        padding: '0 5px',
        color: '#222425',
        fontWeight: 600,
        fontSize: 'small',
        display: 'flex',
        alignItems: 'center',
        height: 35,
        minWidth: 155,
        justifyContent: 'center',
    },
}));

const PatientActivityBox = ({admission, expandFromParent, onOpen, disabledAdmission}) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const theme = useTheme();

    const [location, loadingLocations] = usePatientLocation(admission);

    const splitScreen = useSelector(ctxSplitScreen);
    const isSSEnabled = splitScreen?.enabled;

    const [expand, setExpand] = useState(false);
    const [activityColor, setActivityColor] = useState('');
    const [activityText, setActivityText] = useState('');

    const canView = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_VIEW_ATTENDANCE_HISTORY, Permissions.AGYLE_CAN_EDIT_ATTENDANCE_HISTORY]));

    // Configs
    const uhdMaps = getConfig('modules.map.customMapConfig', []);

    useEffect(() => {
        let color, text;
        switch (admission?.admission?.attendanceType) {
            case 'E':
                color = theme.palette.attendanceType.ed;
                text = t('Emergency Department');
                break;
            case 'I':
                color = theme.palette.attendanceType.ip;
                text = t('Inpatient');
                break;
            case 'O':
                color = theme.palette.attendanceType.op;
                text = t('Outpatient');
                break;
            default:
                console.warn('Wrong attendance type', admission);
                color = '#222222';
                text = 'Unknown type';
                break;
        }
        setActivityColor(color);
        setActivityText(text);
    }, [admission]);

    useEffect(() => {
        setExpand(expandFromParent);
    }, [expandFromParent]);

    const getHeader = () => {
        let facility = null;
        if (admission?.admission?.facility) {
            const hospital = _.find(uhdMaps, map => map?.hospitalId === admission?.admission?.facility);
            if (hospital) {
                facility = (
                    <div className={classes.divRow} style={{gap: 5}}>
                        <ApartmentIcon/>

                        <span>
                            {hospital?.name}
                        </span>
                    </div>
                );
            }
        }

        const age = getAdmissionStartDate(admission?.admission)?.diff(admission?.patient?.dob, 'years');

        return (
            <div className={clsx(classes.divRow, classes.attendanceHeader)} style={{gap: 10, flexWrap: isSSEnabled ? 'wrap' : 'nowrap'}}>
                <Typography variant="subtitle1">
                    {activityText || ''}
                </Typography>

                {facility &&
                    <div className={clsx(classes.divRow)}>
                        {facility}
                    </div>
                }

                <span className={classes.headerText}>
                    {admission?.admission?.attendanceId || '-'}
                </span>

                <span className={classes.headerText}>
                    {t('Age at visit:')} {age}
                </span>


                <div className={clsx(classes.divRow, 'right')} style={{gap: 10}}>
                    {(!loadingLocations && location?.__id) &&
                        <div className={classes.location}>
                            {location?.trudDescription}
                        </div>
                    }

                    <AgyleButton
                        variant="contained"
                        disabled={disabledAdmission || !canView || admission?.__id === null}
                        onClick={() => onOpen(admission, admission?.__id === null)}
                    >
                        {t('Review')}
                    </AgyleButton>

                    <IconButton
                        onClick={() => setExpand(!expand)}
                    >
                        {expand ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                    </IconButton>
                </div>
            </div>
        );
    };

    return (
        <div className={classes.attendanceListItem} style={{borderLeft: `5px solid ${activityColor}`}}>
            {getHeader()}

            <AttendanceTimelineHistory
                admission={admission}
                expanded={expand}
            />
        </div>
    );
};

export default PatientActivityBox;